import type { Line } from "./commands";

/**
 * Tab completion: finish what can be finished, and say what cannot.
 *
 * A pure module, like `commands.ts` and `ticker.ts` — it takes the line, a
 * catalogue of what exists, and returns the new line plus anything to print.
 * It performs no lookup of its own, which is what keeps it testable without a
 * DOM and stops Tab quietly becoming a place that fetches.
 *
 * **Two rules, both borrowed from a shell**, because a terminal that completes
 * differently from every other terminal is worse than one that does not
 * complete at all:
 *
 * 1. Complete to the **longest common prefix**, never to the first match. The
 *    old behaviour took `COMMAND_NAMES.find(startsWith)` — so `s` became
 *    `sell`, silently, and `setup` was undiscoverable by the one key a user
 *    presses to discover things. Worse, which one it picked depended on source
 *    order, so it would have changed the day someone reordered the file.
 * 2. When a prefix is ambiguous and cannot be extended, **list the candidates**
 *    rather than doing nothing. Silence is indistinguishable from a broken key.
 *
 * Since `M7/I3` what may follow a command is positional — `dca on` offers its
 * own flags and `dca log` none — and a value nobody can complete, a price or an
 * order id, prints the argument's one-line hint instead of nothing
 * (`DESIGN-cli-command-spec`).
 */

export type CompletionCatalog = {
  /** Every command name, sorted. */
  commands: readonly string[];
  /** What may follow a command and the tokens after it: words, and a hint for a free value. */
  options: (
    command: string,
    typed: readonly string[],
    partial: string,
  ) => { words: readonly string[]; hint?: string };
};

export type Completion = {
  /** The line as it should now read. Unchanged when nothing could be added. */
  input: string;
  /** Candidates or a hint to print, when nothing could be added. */
  lines?: Line[];
};

/** The longest string that starts all of them, or "" when they share nothing. */
export function commonPrefix(words: readonly string[]): string {
  if (words.length === 0) return "";
  let prefix = words[0] as string;
  for (const word of words.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < word.length && prefix[i] === word[i]) i++;
    prefix = prefix.slice(0, i);
  }
  return prefix;
}

export function complete(input: string, catalog: CompletionCatalog): Completion {
  // Trailing whitespace means "I have finished that word" — the token being
  // completed is the empty one after it, which offers everything.
  const endsWithSpace = /\s$/.test(input);
  const tokens = input
    .trimStart()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  const head = (tokens[0] ?? "").toLowerCase();
  const completingFirst = tokens.length === 0 || (tokens.length === 1 && !endsWithSpace);

  const partial = endsWithSpace ? "" : ((tokens.at(-1) ?? "") as string);
  let options: readonly string[];
  let hint: string | undefined;
  if (completingFirst) {
    options = catalog.commands;
  } else {
    const typed = tokens.slice(1, endsWithSpace ? undefined : -1);
    const next = catalog.options(head, typed, partial);
    options = next.words;
    hint = next.hint;
  }

  const matches = [...new Set(options)].filter((o) =>
    o.toLowerCase().startsWith(partial.toLowerCase()),
  );
  if (matches.length === 0) {
    return hint === undefined
      ? { input }
      : {
          input,
          lines: [
            { kind: "echo", text: input },
            { kind: "note", text: `  ${hint}` },
          ],
        };
  }

  // The one match completes, with a trailing space: the next thing a user wants
  // after `login` is to type the argument, not to add the space themselves.
  if (matches.length === 1) {
    return { input: replaceLast(input, partial, `${matches[0] as string} `) };
  }

  const shared = commonPrefix(matches);
  if (shared.length > partial.length) {
    // Extended, but still ambiguous: no trailing space, because the word is
    // not finished. Nothing is listed — the line moved, which is feedback.
    return { input: replaceLast(input, partial, shared) };
  }

  // Cannot extend. Say what the alternatives are rather than doing nothing.
  return {
    input,
    lines: [
      { kind: "echo", text: input },
      { kind: "note", text: `  ${matches.join("  ")}` },
    ],
  };
}

/**
 * Swap the token being completed for the completed one.
 *
 * Written as a tail replacement rather than by rebuilding the line from tokens,
 * so a user's own spacing survives: re-joining would silently reformat
 * `buy  10   AAPL` on every Tab.
 */
function replaceLast(input: string, partial: string, replacement: string): string {
  if (partial === "") return input + replacement;
  return input.slice(0, input.length - partial.length) + replacement;
}
